import { FC, useState, useEffect, useMemo } from 'react';
import { Prospect, StatusClientEnum } from 'types';
import CustomDataGrid from 'components/CustomDataGrid';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PROMOTERS_BY_STATUS } from '../queries';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import MenuButton from './MenuButton';
import {
  FormattedDateEnum,
  formattedDate,
  removeAccents,
  getInitialStateGrid,
  INITIAL_ROWS_PER_PAGE,
  optionsRowsPerPage,
  convertToMexicoCentralTime,
} from 'utils';
import { NetworkError, NoData, RequestError } from 'components/CustomDataGrid/EmptyStates';
import dayjs from 'dayjs';

const NO_PROSPECTS = 0;
const STEP_ID = 2;

type Props = {
  totalItems: number;
  filter: string;
  actualStep: number;
};

const Accepted: FC<Props> = ({ totalItems, filter, actualStep }) => {
  const [allProspects, setAllProspects] = useState<Prospect[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE);
  const navigate = useNavigate();
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);
  const initialStateGrid = getInitialStateGrid({ page, rowsPerPage });

  const { loading, data, error, refetch } = useQuery(GET_PROMOTERS_BY_STATUS, {
    fetchPolicy: 'no-cache',
    variables: {
      page: 0,
      limit: 10000,
      search: '',
      orderBy: 'createdAt',
      statuses: [StatusClientEnum.ACCEPTED],
    },
  });

  useEffect(() => {
    if (!loading && data) {
      const allProspectsData = data?.getClientsListByStatus || [];

      // Filtrar solo prospectos de los últimos 30 días
      // const now = new Date();
      // const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // const filteredProspects = allProspectsData.filter((prospect: Prospect) => {
      //   if (!prospect.acceptedAt) return false;

      //   const acceptedDate = new Date(prospect.acceptedAt);
      //   return acceptedDate >= thirtyDaysAgo && acceptedDate <= now;
      // });

      // setAllProspects(filteredProspects);
      setAllProspects(allProspectsData);
    }
  }, [data, loading]);

  const filteredProspects = useMemo(() => {
    if (!filter) return allProspects;
    return allProspects.filter((p) => {
      const fullName = `${p.firstName} ${p.firstLastName} ${p.secondLastName}`.toLowerCase();
      const promoter = (p.createdBy || '').toLowerCase();
      const f = filter.toLowerCase();
      return fullName.includes(f) || promoter.includes(f);
    });
  }, [allProspects, filter]);

  const paginatedProspects = useMemo(
    () => filteredProspects.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredProspects, page, rowsPerPage],
  );

  const acceptedColumns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Nombre',
      flex: 1.2,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { firstName, firstLastName, secondLastName } = row;
        const fullName = removeAccents(`${firstName} ${firstLastName} ${secondLastName}`);
        return `${fullName}`;
      },
      renderCell: ({ row }: GridRenderCellParams) => {
        const { firstName, firstLastName, secondLastName } = row;
        return <div>{`${firstName} ${firstLastName} ${secondLastName}`}</div>;
      },
    },
    {
      field: 'createdBy',
      headerName: 'Promotora',
      flex: 1.2,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { createdBy } = row;
        return createdBy;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de solicitud',
      flex: 0.7,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        if (!value) return '';
        return dayjs.utc(value).tz('America/Mexico_City').format('DD/MM/YYYY HH:mm');
      },
    },
    {
      field: 'acceptedAt',
      headerName: 'Fecha de aceptación',
      flex: 0.7,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        if (!value) return '';
        return dayjs.utc(value).tz('America/Mexico_City').format('DD/MM/YYYY HH:mm');
      },
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 0.5,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      align: 'right',
      getActions: ({ row }: GridRowParams) => [
        <MenuButton
          key={row.id}
          handleSeeRequest={() => {
            navigate(`/validate/${row.id}`);
          }}
          idProspect={row.id}
        />,
      ],
    },
  ];

  const handleChangePage = ({ page, pageSize }: GridPaginationModel) => {
    setPage(page);
    setRowsPerPage(pageSize);
  };

  useEffect(() => {
    if (actualStep === STEP_ID) {
      setPage(0);
    }
  }, [actualStep]);

  if (!isOnlineState) return <NetworkError handleRetry={refetch} />;
  if (error) return <RequestError handleRetry={refetch} />;
  if (allProspects.length === 0 && loading === false)
    return <NoData message="Todavía no tienes ningún prospecto aceptado" />;

  return (
    <CustomDataGrid
      loading={loading}
      data={paginatedProspects}
      dataColumns={acceptedColumns}
      nameFilter={filter}
      title="Listado de prospectos aceptados"
      totalRows={filteredProspects.length}
      handleChangePage={handleChangePage}
      initialState={initialStateGrid}
      page={page}
      pageSize={rowsPerPage}
      pageSizeOptions={optionsRowsPerPage}
    />
  );
};

export default Accepted;
